import type { MouseEvent as ReactMouseEvent, ReactNode } from 'react';

import { Icon } from '../Icon';
import styles from './DataTable.module.scss';

export type DataTableColumn<T> = {
  readonly key: string;
  readonly header: ReactNode;
  readonly render: (row: T) => ReactNode;
  readonly align?: 'left' | 'right' | 'center';
  readonly width?: string;
  readonly hideBelow?: 'sm' | 'md';
  readonly className?: string;
};

type DataTableProps<T> = {
  readonly columns: readonly DataTableColumn<T>[];
  readonly rows: readonly T[];
  readonly rowKey: (row: T) => string;
  readonly onRowClick?: (row: T) => void;
  readonly onRowDoubleClick?: (row: T) => void;
  readonly onRowContextMenu?: (row: T, event: ReactMouseEvent<HTMLTableRowElement>) => void;
  readonly isLoading?: boolean;
  readonly skeletonRows?: number;
  readonly emptyMessage?: ReactNode;
  readonly footer?: ReactNode;
};

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  onRowDoubleClick,
  onRowContextMenu,
  isLoading = false,
  skeletonRows = 5,
  emptyMessage,
  footer,
}: DataTableProps<T>) {
  return (
    <div className={styles.wrapper}>
      <div className={styles.scroll}>
        <table className={styles.table}>
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={alignClass(col.align) + ' ' + (col.className ?? '')}
                  style={col.width ? { width: col.width } : undefined}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: skeletonRows }, (_, i) => (
                <tr key={`skeleton-${i}`}>
                  <td colSpan={columns.length} style={{ padding: 0 }}>
                    <div className={styles.skeletonRow} />
                  </td>
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className={styles.emptyRow}>
                  {emptyMessage ?? 'No data'}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={rowKey(row)}
                  className={onRowClick || onRowDoubleClick ? styles.clickable : ''}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  onDoubleClick={onRowDoubleClick ? () => onRowDoubleClick(row) : undefined}
                  onContextMenu={
                    onRowContextMenu ? (event) => onRowContextMenu(row, event) : undefined
                  }
                >
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={alignClass(col.align) + ' ' + (col.className ?? '')}
                    >
                      {col.render(row)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {footer}
    </div>
  );
}

function alignClass(align?: 'left' | 'right' | 'center'): string {
  switch (align) {
    case 'right':
      return styles.alignRight;
    case 'center':
      return styles.alignCenter;
    default:
      return '';
  }
}

type PaginationFooterProps = {
  readonly page: number;
  readonly pageSize: number;
  readonly total: number;
  readonly pageSizeOptions?: readonly number[];
  readonly onPageChange: (page: number) => void;
  readonly onPageSizeChange: (size: number) => void;
  readonly labels?: {
    readonly rowsPerPage?: string;
    readonly rangeOf?: (from: number, to: number, total: number) => string;
    readonly previous?: string;
    readonly next?: string;
  };
};

export function PaginationFooter({
  page,
  pageSize,
  total,
  pageSizeOptions = [10, 25, 50],
  onPageChange,
  onPageSizeChange,
  labels,
}: PaginationFooterProps) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  const rangeOf =
    labels?.rangeOf ?? ((f, t, T) => `${f}–${t} of ${T}`);

  return (
    <div className={styles.footer}>
      <div className={styles.pageControls}>
        <span>{labels?.rowsPerPage ?? 'Rows per page'}</span>
        <select
          className={styles.sizeSelect}
          value={pageSize}
          onChange={(event) => onPageSizeChange(Number.parseInt(event.target.value, 10))}
        >
          {pageSizeOptions.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </div>
      <div className={styles.pageControls}>
        <span>{rangeOf(from, to, total)}</span>
        <button
          type="button"
          className={styles.pageBtn}
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          aria-label={labels?.previous ?? 'Previous page'}
        >
          <Icon name="arrow-back" size={14} />
        </button>
        <button
          type="button"
          className={`${styles.pageBtn} ${styles.next}`}
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
          aria-label={labels?.next ?? 'Next page'}
        >
          <Icon name="arrow-back" size={14} />
        </button>
      </div>
    </div>
  );
}
